import { gql } from '@apollo/client';

// ==================== Types ====================

export type OpportunityStage = 'DISCOVERY' | 'PROPOSAL' | 'NEGOTIATION' | 'WON' | 'LOST';

export interface OpportunityOrganization {
  id: string;
  name: string;
  type: string;
}

export interface OpportunityContact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface OpportunityProductInfo {
  id: string;
  sku: string;
  name: string;
  unitPrice: number;
  category: string;
}

export interface OpportunityProduct {
  id: string;
  opportunityId: string;
  productId: string;
  customSku?: string;
  price: number;
  quantity: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  product?: OpportunityProductInfo;
}

export interface Opportunity {
  id: string;
  organizationId: string;
  name: string;
  value: number;
  stage: OpportunityStage;
  probability: number;
  isRecurring: boolean;
  expectedCloseDate: string;
  actualCloseDate?: string;
  lostReason?: string;
  lostToCompetitorName?: string;
  wonNotes?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  organization: OpportunityOrganization;
  primaryContact?: OpportunityContact;
  products: OpportunityProduct[];
}

export interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

export interface PipelineMetrics {
  totalValue: number;
  weightedValue: number;
  count: number;
  byStage: {
    discovery: { count: number; value: number };
    proposal: { count: number; value: number };
    negotiation: { count: number; value: number };
  };
}

// ==================== Query Results ====================

export interface OpportunitiesQueryResult {
  opportunities: {
    items: Opportunity[];
    pageInfo: PageInfo;
  };
}

export interface OpportunityQueryResult {
  opportunity: Opportunity | null;
}

export interface PipelineMetricsResult {
  pipelineMetrics: PipelineMetrics;
}

// ==================== Mutation Results ====================

export interface CreateOpportunityResult {
  createOpportunity: Opportunity;
}

export interface UpdateOpportunityResult {
  updateOpportunity: Opportunity;
}

export interface MarkOpportunityWonResult {
  markOpportunityWon: Opportunity;
}

export interface MarkOpportunityLostResult {
  markOpportunityLost: Opportunity;
}

export interface DeleteOpportunityResult {
  deleteOpportunity: boolean;
}

// ==================== Fragments ====================

export const OPPORTUNITY_FRAGMENT = gql`
  fragment OpportunityFields on Opportunity {
    id
    organizationId
    name
    value
    stage
    probability
    isRecurring
    expectedCloseDate
    actualCloseDate
    lostReason
    lostToCompetitorName
    wonNotes
    notes
    createdAt
    updatedAt
    organization {
      id
      name
      type
    }
    primaryContact {
      id
      firstName
      lastName
      email
    }
    products {
      id
      opportunityId
      productId
      customSku
      price
      quantity
      notes
      createdAt
      updatedAt
      product {
        id
        sku
        name
        unitPrice
        category
      }
    }
  }
`;

// ==================== Queries ====================

export const GET_OPPORTUNITIES = gql`
  ${OPPORTUNITY_FRAGMENT}
  query GetOpportunities(
    $stage: String
    $search: String
    $organizationId: ID
    $limit: Int
    $offset: Int
  ) {
    opportunities(
      stage: $stage
      search: $search
      organizationId: $organizationId
      limit: $limit
      offset: $offset
    ) {
      items {
        ...OpportunityFields
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        totalCount
        totalPages
        currentPage
      }
    }
  }
`;

export const GET_OPPORTUNITY = gql`
  ${OPPORTUNITY_FRAGMENT}
  query GetOpportunity($id: ID!) {
    opportunity(id: $id) {
      ...OpportunityFields
    }
  }
`;

export const GET_PIPELINE_METRICS = gql`
  query GetPipelineMetrics {
    pipelineMetrics {
      totalValue
      weightedValue
      count
      byStage {
        discovery {
          count
          value
        }
        proposal {
          count
          value
        }
        negotiation {
          count
          value
        }
      }
    }
  }
`;

// ==================== Mutations ====================

export const CREATE_OPPORTUNITY = gql`
  ${OPPORTUNITY_FRAGMENT}
  mutation CreateOpportunity($input: CreateOpportunityInput!) {
    createOpportunity(input: $input) {
      ...OpportunityFields
    }
  }
`;

export const UPDATE_OPPORTUNITY = gql`
  ${OPPORTUNITY_FRAGMENT}
  mutation UpdateOpportunity($id: ID!, $input: UpdateOpportunityInput!) {
    updateOpportunity(id: $id, input: $input) {
      ...OpportunityFields
    }
  }
`;

export const MARK_OPPORTUNITY_WON = gql`
  ${OPPORTUNITY_FRAGMENT}
  mutation MarkOpportunityWon($id: ID!, $input: MarkWonInput) {
    markOpportunityWon(id: $id, input: $input) {
      ...OpportunityFields
    }
  }
`;

export const MARK_OPPORTUNITY_LOST = gql`
  ${OPPORTUNITY_FRAGMENT}
  mutation MarkOpportunityLost($id: ID!, $input: MarkLostInput) {
    markOpportunityLost(id: $id, input: $input) {
      ...OpportunityFields
    }
  }
`;

export const DELETE_OPPORTUNITY = gql`
  mutation DeleteOpportunity($id: ID!) {
    deleteOpportunity(id: $id)
  }
`;

// ==================== Product Mutations ====================

export const ADD_OPPORTUNITY_PRODUCT = gql`
  mutation AddOpportunityProduct($input: AddOpportunityProductInput!) {
    addOpportunityProduct(input: $input) {
      id
      opportunityId
      productId
      customSku
      price
      quantity
      notes
      createdAt
      updatedAt
      product {
        id
        sku
        name
        unitPrice
        category
      }
    }
  }
`;

export const UPDATE_OPPORTUNITY_PRODUCT = gql`
  mutation UpdateOpportunityProduct($id: ID!, $input: UpdateOpportunityProductInput!) {
    updateOpportunityProduct(id: $id, input: $input) {
      id
      opportunityId
      productId
      customSku
      price
      quantity
      notes
      createdAt
      updatedAt
      product {
        id
        sku
        name
        unitPrice
        category
      }
    }
  }
`;

export const REMOVE_OPPORTUNITY_PRODUCT = gql`
  mutation RemoveOpportunityProduct($id: ID!) {
    removeOpportunityProduct(id: $id)
  }
`;
